import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { SubmitHandler, useForm } from 'react-hook-form';
import { NodeApiModel } from '../../../../types/models/node.model';
import { useCreateNode, useGetNode, useUpdateNode } from '../../hooks/api/use-ontology.hook';
import { NodeForm } from './NodeForm';
import { getChildType, NodeType } from '../../../../types/constants/node-type';
import { LoadingIndicator } from '../LoadingIndicator';
import { AddEditSurveyCode } from './AddEditSurveyCode';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditNode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { nodeId, ...params } = useParams<{ nodeType: NodeType; nodeId: string }>();

  const {
    node,
    loading: loadingNode,
    error: gettingError,
  } = useGetNode<NodeApiModel>({
    nodeId: location.pathname.endsWith('/edit') && nodeId ? +nodeId : Number.NaN,
    nodeType: params.nodeType as NodeType,
  });
  const [createNode, { loading: creatingNode, error: createError }] = useCreateNode();
  const [updateNode, { loading: updatingNode, error: updateError }] = useUpdateNode();

  const nodeType = node?.type || (getChildType(params.nodeType as NodeType) as NodeType);

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<NodeForm>({ resolver: classValidatorResolver(NodeForm), values: node });

  // Its not ideal, but couldn't find a better way of doing this. Having a custom route for survey codes while
  // Keeping the parent route dynamic doesn't seem to be supported
  if (nodeType === NodeType.MASTER) {
    return <AddEditSurveyCode />;
  }

  const onSubmit: SubmitHandler<NodeForm> = async (submittedValues) => {
    const updatedValues = getDirtyValues(dirtyFields, submittedValues);
    await (node
      ? updateNode({ ...updatedValues, parentId: node.parent?.id, nodeId: node.id, nodeType: node.type })
      : createNode({ ...submittedValues, attachments: [], parentId: nodeId ? +nodeId : undefined, type: nodeType }));

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    return (
      <>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingNode || updatingNode}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
      </>
    );
  };

  if (loadingNode) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingNode ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${node ? 'Edit' : 'Add'} ${nodeType}`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {node ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
